import { collection, addDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Stripe Checkout Service
 * 
 * Handles creation of Stripe Checkout sessions using the Stripe Firebase Extension.
 * The extension works by creating Firestore documents that trigger checkout session creation.
 */

/**
 * Create a Stripe Checkout session and redirect the user
 * 
 * This uses the Stripe Firebase Extension's Firestore-based approach:
 * 1. Creates a document in the checkout_sessions subcollection
 * 2. The extension watches for this document and creates the Stripe checkout session
 * 3. We listen for the checkout session URL in Firestore
 * 4. Redirects the user to Stripe Checkout
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.priceId - Stripe Price ID (e.g., 'price_1234567890')
 * @param {string} options.userId - Firebase User ID (required)
 * @param {string} [options.successUrl] - URL to redirect to after successful payment (defaults to current page)
 * @param {string} [options.cancelUrl] - URL to redirect to if user cancels (defaults to current page)
 * @param {boolean} [options.redirect=true] - Whether to automatically redirect (default: true)
 * @returns {Promise<string>} - The checkout URL (if redirect is false)
 * 
 * @example
 * // Basic usage - redirects automatically
 * await stripeCheckoutService.createSession({ 
 *   priceId: 'price_1234567890',
 *   userId: currentUser.uid 
 * });
 */
export const stripeCheckoutService = {
  async createSession(options) {
    const { priceId, userId, successUrl, cancelUrl, redirect = true } = options;

    if (!priceId || typeof priceId !== 'string') {
      throw new Error('Price ID is required and must be a string');
    }

    if (!userId || typeof userId !== 'string') {
      throw new Error('User ID is required and must be a string');
    }

    try {
      // Build URLs - defaults to current page
      const defaultSuccessUrl = successUrl || window.location.origin + '/manage-subscription?success=true';
      const defaultCancelUrl = cancelUrl || window.location.origin + '/manage-subscription?canceled=true';
      
      // Create checkout session document in Firestore
      // The Stripe Firebase Extension watches this collection and creates the Stripe checkout session
      const checkoutSessionsRef = collection(
        db,
        'stripe_customers',
        userId,
        'checkout_sessions'
      );

      // Create the checkout session document
      // The extension expects: price (or priceId), success_url, cancel_url, mode
      const checkoutSessionDoc = await addDoc(checkoutSessionsRef, {
        price: priceId,
        success_url: defaultSuccessUrl,
        cancel_url: defaultCancelUrl,
        mode: 'subscription',
      });

      // Wait for the extension to populate the checkout session URL
      // The extension will update the document with a 'url' field when ready
      return new Promise((resolve, reject) => {
        let unsubscribe = null;
        
        // Set up a timeout (30 seconds)
        const timeout = setTimeout(() => {
          if (unsubscribe) unsubscribe();
          reject(new Error('Checkout session creation timed out. Please try again.'));
        }, 30000);

        // Listen for the checkout session document to be updated with the URL
        const checkoutSessionDocRef = doc(checkoutSessionsRef, checkoutSessionDoc.id);
        unsubscribe = onSnapshot(
          checkoutSessionDocRef,
          (snapshot) => {
            if (!snapshot.exists()) {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error('Checkout session document was deleted.'));
              return;
            }

            const data = snapshot.data();
            
            // Check for errors from the extension first
            if (data.error) {
              clearTimeout(timeout);
              if (unsubscribe) unsubscribe();
              reject(new Error(data.error.message || 'Failed to create checkout session'));
              return;
            }
            
            // Check if the extension has populated the URL
            if (data.url) {
              clearTimeout(timeout);
              if (unsubscribe) unsubscribe();
              
              const checkoutUrl = data.url;
              
              // Validate URL format
              try {
                new URL(checkoutUrl);
              } catch (urlError) {
                reject(new Error('Invalid URL format returned from Stripe checkout'));
                return;
              }

              // Redirect if requested (default behavior)
              if (redirect) {
                window.location.href = checkoutUrl;
                resolve(checkoutUrl);
              } else {
                resolve(checkoutUrl);
              }
            }
          },
          (error) => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(`Failed to listen for checkout session: ${error.message}`));
          }
        );
      });
    } catch (error) {
      // Handle Firestore errors
      if (error.code) {
        switch (error.code) {
          case 'permission-denied':
            throw new Error(
              'Permission denied. Please ensure you are logged in and have permission to create checkout sessions.'
            );
          case 'unauthenticated':
            throw new Error(
              'You must be logged in to start a subscription.'
            );
          case 'invalid-argument':
            throw new Error(
              'Invalid price ID. Please try again or contact support.'
            );
          case 'deadline-exceeded':
            throw new Error(
              'Request timed out. Please try again.'
            );
          case 'resource-exhausted':
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          case 'failed-precondition':
            throw new Error(
              'Unable to create checkout session. Please try again.'
            );
          case 'aborted':
            throw new Error(
              'Request was aborted. Please try again.'
            );
          case 'out-of-range':
            throw new Error(
              'Invalid request parameters. Please try again.'
            );
          case 'unimplemented':
            throw new Error(
              'This feature is not available. Please contact support.'
            );
          case 'internal':
            throw new Error(
              'An internal error occurred. Please try again later or contact support.'
            );
          case 'unavailable':
            throw new Error(
              'Service is temporarily unavailable. Please try again later.'
            );
          case 'data-loss':
            throw new Error(
              'Data error occurred. Please try again or contact support.'
            );
          default:
            // For other Firestore errors, use the message if available
            if (error.message) {
              throw new Error(error.message);
            }
            throw new Error(
              `Failed to create checkout session: ${error.code || 'Unknown error'}`
            );
        }
      }

      // Handle network errors
      if (error.message && error.message.includes('network')) {
        throw new Error(
          'Network error. Please check your internet connection and try again.'
        );
      }

      // Handle timeout errors
      if (error.message && (error.message.includes('timeout') || error.message.includes('deadline'))) {
        throw new Error(
          'Request timed out. Please try again.'
        );
      }

      // Handle generic errors
      if (error.message) {
        throw error;
      }

      // Fallback for unknown errors
      throw new Error(
        'An unexpected error occurred while creating the checkout session. Please try again or contact support.'
      );
    }
  },
};

export default stripeCheckoutService;

